import { createApp, nextTick } from 'vue'
import App from './App.vue'
import router from './router'
import { bootstrapAppAuth, saveAuthFromLocationSearch } from '@/utils/auth'
import { getBridge } from '@/utils/bridge'
import { installRNBridgeShim } from '@/utils/rn-bridge-shim'
import { initVConsole } from '@/utils/vconsole'
import { logStartupDiagnostics } from '@/version'
import './styles/reset.css'
import './styles/navigation.css'

// 鉴权握手最长等待时间:超过则先渲染,避免 RN 桥接异常(如 ready 状态竞态)导致永久白屏
const AUTH_BOOTSTRAP_TIMEOUT_MS = 2500

function mountApp() {
  createApp(App).use(router).mount('#app')
  // 路由组件是异步下载的；等真实首屏渲染后交接给页内 loading，避免中途露出空白。
  void router.isReady().then(async () => {
    await nextTick()
    document.getElementById('customer-search-boot')?.remove()
  }).catch(() => {
    const label = document.querySelector('#customer-search-boot p')
    if (label) label.textContent = '页面加载失败，请返回重试'
  })
}

async function startApp() {
  // Android WebView 标记：用于跳过 sticky translateZ 等易触发合成层纹理丢失的样式
  if (/Android/i.test(navigator.userAgent)) {
    document.documentElement.classList.add('platform-android')
  }
  // 先装 RNBridge shim:Android 8 系统 WebView 上 RN 端 injectedJavaScriptBeforeContentLoaded
  // 注入失效,导致 window.RNBridge 不存在;shim 在 H5 侧按相同协议补一份,不影响 iOS / 高版本 Android
  installRNBridgeShim()
  initVConsole()
  const bridge = getBridge()
  console.log('[H5Diag] startApp begin', {
    href: window.location.href,
    hasRNBridge: bridge ? 'object' : 'undefined',
    hasInvokeNative: typeof bridge?.invokeNative,
    ua: navigator.userAgent,
    t: Date.now(),
  })
  // 浏览器 ?token= 须在首屏请求前写入 localStorage
  saveAuthFromLocationSearch()
  logStartupDiagnostics()

  // 正常情况下 bootstrap 仅需几十 ms；一旦桥接卡死则最多等待指定时间。
  const authBootstrapTask = Promise.race([
    bootstrapAppAuth()
      .then(() => console.log('[JellyTech H5] bootstrapAppAuth done'))
      .catch((error) => console.warn('[JellyTech H5] bootstrapAppAuth failed', error)),
    new Promise<void>((resolve) => window.setTimeout(resolve, AUTH_BOOTSTRAP_TIMEOUT_MS)),
  ])

  // 这些页面都有自己的 loading 态，应先挂载再等鉴权，避免新 WebView 首次进入时白屏 2.5 秒；
  // 页面请求会复用 bootstrapAppAuth() 的同一个 Promise 等待 token 就绪。
  const currentPath = window.location.pathname.replace(/\/+$/, '')
  if (
    ['/duration-usage', '/duration-store', '/contact-service'].includes(currentPath) ||
    currentPath.startsWith('/customer-detail') ||
    currentPath.startsWith('/customer-search') ||
    currentPath.startsWith('/customer-ownership') ||
    currentPath.startsWith('/duration-purchase-history')
  ) {
    mountApp()
    return
  }

  await authBootstrapTask
  mountApp()
}

startApp()
